import { createSelector } from 'reselect';

import { accountsSelectors } from '../accounts';
import type { Account } from '../accounts/accountsTypes';
import { authSelectors } from '../auth';
import { featureFlagsSelectors } from '../feature-flags';
import { institutionLoginsSelectors } from '../institution-logins';
import type { InstitutionLogin } from '../institution-logins/institutionLoginsTypes';
import type { AggAction } from './accountDiscoveryTypes';
import { getAggregationAction, getAggregationActionForLogin } from './aggErrors/aggErrorsSelectors';
import type { FiMigrationOffer, FiMigrationSummaryOffer } from './fiMigrationTypes';
import {
  isAnyFiMigrationAction,
  isFiMigrationAction,
  isForcedFiMigrationAction,
  withFiMigrationEntry,
} from './fiMigrationUtils';

export const getFiMigrationSessionKey = createSelector(
  authSelectors.getQCSId,
  authSelectors.getDatasetId,
  (userId, datasetId): string | null => (userId && datasetId ? JSON.stringify([userId, datasetId]) : null),
);

// Eligibility telemetry follows the backend signal, independently of destination and open-account availability.
export const getFiMigrationEligibleLogins = createSelector(
  institutionLoginsSelectors.getInstitutionLoginsById,
  (logins): InstitutionLogin[] =>
    logins
      .valueSeq()
      .filter((login) => !login.isDeleted && login.optionalAction === 'FI_MIGRATION')
      .toArray(),
);

// A login is offered when its current action is the migration and it has open accounts to move.
const toMigrationOffers = (
  logins: readonly InstitutionLogin[],
  accountsByLoginId: ReturnType<typeof accountsSelectors.getAccountsByInstitutionLoginId>,
  isMigrationAction: (action: AggAction | null | undefined) => boolean,
): FiMigrationOffer[] =>
  logins.flatMap((institutionLogin) => {
    const accounts = accountsByLoginId.get(institutionLogin.id);
    if (
      !isMigrationAction(
        getAggregationActionForLogin(institutionLogin, accounts, featureFlagsSelectors.dangerouslyUseFeatureFlags()),
      )
    ) {
      return [];
    }
    const accountCount = accounts?.count((account) => !account.isClosed && !account.isDeleted) || 0;
    return accountCount ? [{ institutionLogin, accountCount }] : [];
  });

export const getFiMigrationOffers = createSelector(
  getFiMigrationEligibleLogins,
  accountsSelectors.getAccountsByInstitutionLoginId,
  (logins, accountsByLoginId): FiMigrationOffer[] => toMigrationOffers(logins, accountsByLoginId, isFiMigrationAction),
);

const countInstitutions = (offers: readonly FiMigrationOffer[]): number =>
  new Set(offers.map(({ institutionLogin }) => institutionLogin.institutionId || institutionLogin.id)).size;

export const getFiMigrationInstitutionCount = createSelector(getFiMigrationOffers, countInstitutions);

type FiMigrationState = Parameters<typeof getFiMigrationOffers>[0];

const NO_OFFERS: readonly FiMigrationOffer[] = [];
const NO_SUMMARY_OFFERS: readonly FiMigrationSummaryOffer[] = [];

export const getForcedFiMigrationOffers = createSelector(
  institutionLoginsSelectors.getInstitutionLoginsById,
  accountsSelectors.getAccountsByInstitutionLoginId,
  (logins, accountsByLoginId): FiMigrationOffer[] =>
    toMigrationOffers(
      logins
        .valueSeq()
        .filter((login) => !login.isDeleted)
        .toArray(),
      accountsByLoginId,
      isForcedFiMigrationAction,
    ),
);

// The summary surfaces list forced migrations first, but only alongside an optional offer. A forced migration alone
// keeps to the disconnected account surfaces.
export const getFiMigrationSummaryOffers = createSelector(
  getFiMigrationOffers,
  // Any login can be in forced migration, so search them only when there is an optional offer to list them with.
  (state: FiMigrationState): readonly FiMigrationOffer[] =>
    getFiMigrationOffers(state).length ? getForcedFiMigrationOffers(state) : NO_OFFERS,
  (optional, forced): readonly FiMigrationSummaryOffer[] =>
    optional.length
      ? [
          ...forced.map((offer): FiMigrationSummaryOffer => ({ ...offer, migrationType: 'forced' })),
          ...optional.map((offer): FiMigrationSummaryOffer => ({ ...offer, migrationType: 'optional' })),
        ]
      : NO_SUMMARY_OFFERS,
);

export const getFiMigrationSummaryInstitutionCount = createSelector(getFiMigrationSummaryOffers, countInstitutions);

const selectLoginId = (_state: FiMigrationState, institutionLoginId: string | undefined): string | undefined =>
  institutionLoginId;

const findOfferForLogin = <Offer extends FiMigrationOffer>(
  offers: readonly Offer[],
  institutionLoginId: string | undefined,
): Offer | undefined => offers.find(({ institutionLogin }) => institutionLogin.id === institutionLoginId);

export const getFiMigrationOfferByLogin = createSelector(getFiMigrationOffers, selectLoginId, findOfferForLogin);

export const getFiMigrationSummaryOfferByLogin = createSelector(
  getFiMigrationSummaryOffers,
  selectLoginId,
  findOfferForLogin,
);

export const getFiMigrationOfferByAccount = createSelector(
  (state: FiMigrationState, accountId: string | undefined): Account | undefined =>
    accountsSelectors.getAccountById(state, accountId),
  (state: FiMigrationState, accountId: string | undefined): FiMigrationOffer | undefined =>
    getFiMigrationOfferByLogin(
      state,
      accountsSelectors.getAccountById(state, accountId)?.institutionLoginId || undefined,
    ),
  (account, offer): FiMigrationOffer | undefined =>
    account && !account.isClosed && !account.isDeleted ? offer : undefined,
);

const selectLoginAction = (
  state: FiMigrationState,
  institutionLoginId: string | undefined,
): AggAction | null | undefined => getAggregationAction(state, { institutionLoginId: institutionLoginId || '' });

const selectEntry = (_state: FiMigrationState, _institutionLoginId: string | undefined, entry: string): string => entry;

const toOfferedMigrationAction = (
  offer: FiMigrationOffer | undefined,
  action: AggAction | null | undefined,
  entry: string,
): AggAction | undefined =>
  offer && action && isAnyFiMigrationAction(action) ? withFiMigrationEntry(action, entry) : undefined;

export const getFiMigrationAction = createSelector(
  getFiMigrationOfferByLogin,
  selectLoginAction,
  selectEntry,
  toOfferedMigrationAction,
);

export const getFiMigrationSummaryAction = createSelector(
  getFiMigrationSummaryOfferByLogin,
  selectLoginAction,
  selectEntry,
  toOfferedMigrationAction,
);
